import { Element, Task } from "../types";

/**
 * Utilities for manipulating tree structures of elements (tasks, subtasks, etc.)
 */

const isTask = (node: Element): node is Task => node.type === 'task' || !node.type;

const getSubtasks = (node: Element): Task[] | undefined =>
    isTask(node) ? node.subtasks : undefined;

const withSubtasks = (node: Element, subtasks: Task[]): Element => ({
    ...(node as Task),
    subtasks,
} as Element);

type ElementWithTreeMetadata = Element & {
    children?: ElementWithTreeMetadata[];
    canHaveChildren?: unknown;
    parent?: unknown;
    parentId?: unknown;
    depth?: unknown;
    index?: unknown;
    isLast?: unknown;
};

const TRANSIENT_TREE_KEYS = [
    'children',
    'canHaveChildren',
    'parent',
    'parentId',
    'depth',
    'index',
    'isLast',
] as const;

/**
 * Removes dnd-kit metadata before an element tree reaches application state or
 * localStorage. The function preserves references when the input is already
 * clean so callers can safely use it during React state updates.
 */
export const sanitizeElementTree = (
    nodes: Element[],
    preserveRootGroupIds = true,
): Element[] => {
    const cleanNode = (node: ElementWithTreeMetadata, isRoot: boolean): Element => {
        const record = node as ElementWithTreeMetadata & Record<string, unknown>;
        const taskNode = isTask(node);
        const children = Array.isArray(record.children)
            ? record.children as ElementWithTreeMetadata[]
            : [];
        const storedSubtasks = taskNode && Array.isArray(node.subtasks)
            ? node.subtasks as ElementWithTreeMetadata[]
            : undefined;
        const subtaskSource = storedSubtasks || children;
        const cleanedSubtasks = taskNode
            ? sanitizeElementTree(subtaskSource as Element[], false) as Task[]
            : [];
        const hasTransientMetadata = TRANSIENT_TREE_KEYS.some((key) =>
            Object.prototype.hasOwnProperty.call(record, key),
        );
        const shouldRemoveGroupId = !isRoot || !preserveRootGroupIds;
        const hasStaleGroupId = shouldRemoveGroupId && Object.prototype.hasOwnProperty.call(record, 'groupId');
        const hasInvalidTaskFields = !taskNode && (
            Object.prototype.hasOwnProperty.call(record, 'subtasks') ||
            Object.prototype.hasOwnProperty.call(record, 'collapsed')
        );
        const subtasksChanged = taskNode && (
            (storedSubtasks === undefined && children.length > 0) ||
            cleanedSubtasks !== subtaskSource
        );

        if (!hasTransientMetadata && !hasStaleGroupId && !hasInvalidTaskFields && !subtasksChanged) {
            return node;
        }

        const cleaned = { ...record };
        TRANSIENT_TREE_KEYS.forEach((key) => delete cleaned[key]);
        if (shouldRemoveGroupId) delete cleaned.groupId;

        if (taskNode) {
            cleaned.subtasks = cleanedSubtasks;
        } else {
            delete cleaned.subtasks;
            delete cleaned.collapsed;
        }

        return cleaned as unknown as Element;
    };

    let changed = false;
    const cleanedNodes = (nodes || []).map((node) => {
        const cleanedNode = cleanNode(node as ElementWithTreeMetadata, true);
        changed = changed || cleanedNode !== node;
        return cleanedNode;
    });

    return changed ? cleanedNodes : nodes;
};

/**
 * Assigns a top-level element to a section and removes transient tree metadata.
 * Subtasks inherit their root task's section and therefore must not persist a
 * separate groupId that can become stale after their parent moves.
 */
export const assignElementToGroup = (
    element: ElementWithTreeMetadata,
    groupId: string,
): Element => {
    const cleaned = sanitizeElementTree([element], true)[0];
    return { ...cleaned, groupId };
};

/**
 * Recursively removes a node by its ID from a tree of nodes.
 * @param nodes - The tree of nodes to search.
 * @param id - The ID of the node to remove.
 * @returns The updated tree of nodes.
 */
export const removeNodeById = (nodes: Element[], id: string): Element[] => {
    const source = nodes || [];
    let changed = false;
    const result = source.reduce((acc: Element[], node) => {
        if (node.id === id) {
            changed = true;
            return acc;
        }

        const subtasks = getSubtasks(node);
        if (!subtasks?.length) {
            acc.push(node);
            return acc;
        }

        const prunedSubtasks = removeNodeById(subtasks, id) as Task[];
        if (prunedSubtasks !== subtasks) {
            changed = true;
            acc.push(withSubtasks(node, prunedSubtasks));
        } else {
            acc.push(node);
        }
        return acc;
    }, []);

    return changed ? result : source;
};

/**
 * Recursively updates a node by its ID in a tree of nodes using an updater function.
 * @param nodes - The tree of nodes to search.
 * @param id - The ID of the node to update.
 * @param updater - A function that takes the node and returns the updated node.
 * @returns The updated tree of nodes.
 */
export const updateNodeById = (nodes: Element[], id: string, updater: (node: Element) => Element): Element[] => {
    const source = nodes || [];
    let changed = false;
    const result = source.map((node) => {
        if (node.id === id) {
            const updatedNode = updater(node);
            changed = changed || updatedNode !== node;
            return updatedNode;
        }

        const subtasks = getSubtasks(node);
        if (!subtasks?.length) return node;

        const updatedSubtasks = updateNodeById(subtasks, id, updater) as Task[];
        if (updatedSubtasks !== subtasks) {
            changed = true;
            return withSubtasks(node, updatedSubtasks);
        }
        return node;
    });

    return changed ? result : source;
};

/**
 * Converts a flat array with subtasks to a format expected by dnd-kit-sortable-tree (children property).
 * @param nodes - The nodes to convert.
 * @returns Nodes with children instead of subtasks.
 */
export const toTreeItems = (nodes: Element[]): { id: string; [key: string]: unknown }[] =>
    (nodes || []).map((n) => ({
        ...n,
        children: toTreeItems((n.type === 'task' || !n.type) ? (n as Task).subtasks || [] : []),
    }));

/**
 * Converts nodes from dnd-kit-sortable-tree format back to internal format (subtasks property).
 * @param nodes - The nodes to convert.
 * @returns Nodes with subtasks instead of children.
 */
export const fromTreeItems = (nodes: { [key: string]: unknown }[]): Element[] => {
    const convert = (items: { [key: string]: unknown }[], isRoot: boolean): Element[] =>
        (items || []).map((n): Element => {
        const { children = [], ...rest } = n;
        delete rest.canHaveChildren;
        if (!isRoot) delete rest.groupId;

        const element = rest as unknown as Element;
        if (isTask(element)) {
            return {
                ...element,
                subtasks: convert(children as { [key: string]: unknown }[], false) as Task[],
            };
        }

        delete rest.subtasks;
        delete rest.collapsed;
        return rest as unknown as Element;
    });

    return sanitizeElementTree(convert(nodes, true), true);
};

/**
 * Recursively replaces a node by its ID in a tree of nodes.
 * @param nodes - The tree of nodes to search.
 * @param updatedNode - The updated node object (must contain an id).
 * @returns The updated tree of nodes.
 */
export const replaceNodeById = (nodes: Element[], updatedNode: Element): Element[] => {
    const { id } = updatedNode;
    const source = nodes || [];
    let changed = false;
    const result = source.map((node) => {
        if (node.id === id) {
            changed = updatedNode !== node;
            return updatedNode;
        }

        const subtasks = getSubtasks(node);
        if (!subtasks?.length) return node;

        const updatedSubtasks = replaceNodeById(subtasks, updatedNode) as Task[];
        if (updatedSubtasks !== subtasks) {
            changed = true;
            return withSubtasks(node, updatedSubtasks);
        }
        return node;
    });

    return changed ? result : source;
};
